/* eslint-disable no-unused-vars */
import { StreamTypes, VideoPlayerStates } from './constants'

/**
 * @typedef {typeof StreamTypes[keyof typeof StreamTypes]} StreamType {@link StreamTypes}
 */

/**
 * @typedef {typeof VideoPlayerStates[keyof typeof VideoPlayerStates]} VideoPlayerState {@link VideoPlayerStates}
 */

/**
 * @typedef {Object} TimelineEvent
 * @property {number | null | undefined} endMillis The timestamp, if any, in milliseconds, with respect to the video's length, that marks the end of this event.
 * @property {string} gameId The identifier of the game the video relates to.
 * @property {number} gameNumber The game's ordinal with respect to the other games within the same match.
 * @property {string} matchId The identifier of the match the video relates to.
 * @property {number | null | undefined} startMillis The timestamp, if any, in milliseconds, with respect to the video's length, that marks the beginning of this event.
 */

export class IVideoPlayer {
  /**
   * Returns the state of the player.
   * @returns {VideoPlayerState}
   */
  getState () {}

  /**
   * Initializes playback for a new video.
   *
   * @param {string} tournamentId The identifier of the tournament the video relates to.
   * @param {string} uuid The identifier of the video.
   * @param {StreamType} streamType The type of video.
   * @param {number | null} startMillis For VODs that span multiple games, the timestamp in milliseconds, with respect to the VOD's length, that marks the beginning of the current timeline event.
   * @param {number | null} endMillis For VODs that span multiple games, the timestamp in milliseconds, with respect to the VOD's length, that marks the end of the current timeline event.
   * @param {Pick<TimelineEvent, 'gameId' | 'gameNumber' | 'matchId'} timelineEvent The specific match and game the video relates to.
   * @param {TimelineEvent[]} timeline A collection of the related match's VODs that match the video player's underlying provider.
   * @param {string} leagueSlug The slug of the league the related tournament is associated with.
   */
  loadById (
    tournamentId,
    uuid,
    streamType,
    startMillis,
    endMillis,
    timelineEvent,
    timeline,
    leagueSlug
  ) {}

  /**
   * Pauses the currently playing video.
   *
   * @deprecated This exists for historical purposes but is no longer actively used.
   */
  pause () {}

  /**
   * Plays the currently loaded video.
   *
   * @deprecated This exists for historical purposes but is no longer actively used.
   */
  play () {}

  /**
   * Disposes the player.
   */
  remove () {}

  /**
   * Displays the players on the UI.
   */
  render () {}

  /**
   * Initializes the player and starts playback for the specified video.
   *
   * @param {string} tournamentId The identifier of the tournament the video relates to.
   * @param {string} uuid The identifier of the video.
   * @param {StreamType} streamType The type of video.
   * @param {number | null} startMillis For VODs that span multiple games, the timestamp in milliseconds, with respect to the VOD's length, that marks the beginning of the current timeline event.
   * @param {number | null} endMillis For VODs that span multiple games, the timestamp in milliseconds, with respect to the VOD's length, that marks the end of the current timeline event.
   * @param {Pick<TimelineEvent, 'gameId' | 'gameNumber' | 'matchId'} timelineEvent The specific match and game the video relates to.
   * @param {TimelineEvent[]} timeline A collection of the related match's VODs that match the video player's underlying provider.
   * @param {string} leagueSlug The slug of the league the related tournament is associated with.
   */
  setup (
    tournamentId,
    uuid,
    streamType,
    startMillis,
    endMillis,
    timelineEvent,
    timeline,
    leagueSlug
  ) {}

  /**
   * For VODs that span multiple games, the timestamp in milliseconds, with respect to the VOD's
   * length, that marks the end of the current timeline event configured for playback.
   * @returns {number | null}
   */
  get endMillis () {}

  /**
   * For VODs that span multiple games, the timestamp in milliseconds, with respect to the VOD's
   * length, that marks the beginning of the current timeline event configured for playback.
   * @returns {number | null}
   */
  get startMillis () {}

  /**
   * The identifier of the video configured for playback.
   */
  get uuid () {}
}
